import {
    ao,
    cci,
    ema,
    hma,
    macd,
    mom,
    rsi,
    sma,
    stoch,
    ultosc,
    vwma,
    willr
} from '../../../util/IndicatorUtils/core/indicators'
import { TechnicalsIndicatorSpec } from '../../../proto/generated/overview-widget'
import { IndicatorComputeResult, TechnicalsCandleSeries } from './types'

const rma = (values: number[], length: number): number[] => {
    const size = values.length
    const rmaValues = new Array(size).fill(NaN)
    if (length < 1) return rmaValues

    const alpha = 1 / length
    let sum = 0
    let validCount = 0
    let prevRma: number | null = null

    for (let i = 0; i < size; i++) {
        const v = values[i]
        if (!Number.isFinite(v)) continue

        if (prevRma === null) {
            sum += v
            validCount++
            if (validCount === length) {
                prevRma = sum / length
                rmaValues[i] = prevRma
            }
            continue
        }

        prevRma = prevRma + alpha * (v - prevRma)
        rmaValues[i] = prevRma
    }

    return rmaValues
}

const computeDmi = (
    high: number[],
    low: number[],
    close: number[],
    diLength: number,
    adxLength: number
): { adx: number[]; plusDI: number[]; minusDI: number[] } => {
    const size = high.length
    const plusDM: number[] = [0]
    const minusDM: number[] = [0]
    const trueRange: number[] = [high[0] - low[0]]

    for (let i = 1; i < size; i++) {
        const upMove = high[i] - high[i - 1]
        const downMove = low[i - 1] - low[i]
        plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0)
        minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0)

        const hl = high[i] - low[i]
        const hc = Math.abs(high[i] - close[i - 1])
        const lc = Math.abs(low[i] - close[i - 1])
        trueRange.push(Math.max(hl, hc, lc))
    }

    const smoothedTR = rma(trueRange, diLength)
    const smoothedPlusDM = rma(plusDM, diLength)
    const smoothedMinusDM = rma(minusDM, diLength)

    const plusDI: number[] = []
    const minusDI: number[] = []
    for (let i = 0; i < smoothedTR.length; i++) {
        if (smoothedTR[i] === 0) {
            plusDI.push(0)
            minusDI.push(0)
        } else {
            plusDI.push((100 * smoothedPlusDM[i]) / smoothedTR[i])
            minusDI.push((100 * smoothedMinusDM[i]) / smoothedTR[i])
        }
    }

    const dx: number[] = []
    for (let i = 0; i < plusDI.length; i++) {
        const sum = plusDI[i] + minusDI[i]
        dx.push(sum === 0 ? 0 : (100 * Math.abs(plusDI[i] - minusDI[i])) / sum)
    }

    return { adx: rma(dx, adxLength), plusDI, minusDI }
}

const lastFiniteIndex = (arr: number[]): number => {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (Number.isFinite(arr[i])) return i
    }
    return -1
}

const last = (arr: number[]): number | undefined => {
    const idx = lastFiniteIndex(arr)
    return idx >= 0 ? arr[idx] : undefined
}

const at = (arr: number[], offsetFromEnd: number): number | undefined => {
    const end = lastFiniteIndex(arr)
    if (end < 0) return undefined
    const idx = end - offsetFromEnd
    if (idx < 0) return undefined
    const v = arr[idx]
    return Number.isFinite(v) ? v : undefined
}

const resolveSource = (source: string, series: TechnicalsCandleSeries): number[] => {
    switch (source) {
        case 'HLC3':
            return series.hlc3
        case 'HL2':
            return series.hl2
        case 'OPEN':
            return series.open
        case 'HIGH':
            return series.high
        case 'LOW':
            return series.low
        default:
            return series.close
    }
}

const inputInt = (inputs: Record<string, string>, ...keys: string[]): number => {
    for (const key of keys) {
        const direct = inputs[key]
        if (direct !== undefined) return parseInt(direct, 10)
        const match = Object.entries(inputs).find(([k]) => k.toLowerCase() === key.toLowerCase())
        if (match) return parseInt(match[1], 10)
    }
    return NaN
}

const donchianMid = (high: number[], low: number[], endIdx: number, length: number): number => {
    let highest = -Infinity
    let lowest = Infinity
    for (let i = endIdx - length + 1; i <= endIdx; i++) {
        highest = Math.max(highest, high[i])
        lowest = Math.min(lowest, low[i])
    }
    return (highest + lowest) / 2
}

const computeIchimoku = (series: TechnicalsCandleSeries, inputs: Record<string, string>) => {
    const conversionLen = inputInt(inputs, 'CONVERSION_LINE_LENGTH') || 9
    const baseLen = inputInt(inputs, 'BASE_LINE_LENGTH') || 26
    const spanBLen = inputInt(inputs, 'LEADING_SPAN_B_LENGTH') || 52
    const endIdx = series.close.length - 1
    const conversion = donchianMid(series.high, series.low, endIdx, conversionLen)
    const base = donchianMid(series.high, series.low, endIdx, baseLen)
    const spanB = donchianMid(series.high, series.low, endIdx, spanBLen)
    const spanA = (conversion + base) / 2
    return {
        value: base,
        extras: {
            CONVERSION_LINE: conversion,
            BASE_LINE: base,
            LEADING_SPAN_A: spanA,
            LEADING_SPAN_B: spanB
        }
    }
}

const smaAligned = (data: number[], period: number): number[] => {
    const out = new Array(data.length).fill(NaN)
    for (let i = period - 1; i < data.length; i++) {
        let sum = 0
        let count = 0
        for (let j = i - period + 1; j <= i; j++) {
            const v = data[j]
            if (Number.isNaN(v)) continue
            sum += v
            count++
        }
        if (count === period) out[i] = sum / period
    }
    return out
}

const computeStochRsi = (close: number[], inputs: Record<string, string>) => {
    const rsiLen = inputInt(inputs, 'rsi_length') || 14
    const stochLen = inputInt(inputs, 'stoch_length') || 14
    const kSmooth = inputInt(inputs, 'k') || 3
    const dSmooth = inputInt(inputs, 'd') || 3

    const rsiSeries = rsi(close, rsiLen)
    const kRaw: number[] = new Array(rsiSeries.length).fill(NaN)
    for (let i = stochLen - 1; i < rsiSeries.length; i++) {
        let hi = -Infinity
        let lo = Infinity
        for (let j = i - stochLen + 1; j <= i; j++) {
            const v = rsiSeries[j]
            if (Number.isNaN(v)) continue
            hi = Math.max(hi, v)
            lo = Math.min(lo, v)
        }
        const curr = rsiSeries[i]
        if (Number.isNaN(curr) || hi === -Infinity || lo === Infinity) continue
        kRaw[i] = hi === lo ? 0 : (100 * (curr - lo)) / (hi - lo)
    }

    const kSeries = smaAligned(kRaw, kSmooth)
    const dSeries = smaAligned(kSeries, dSmooth)

    return {
        value: last(kSeries),
        previous: at(kSeries, 1),
        extras: {
            '%D': last(dSeries) ?? NaN
        }
    }
}

const computeBullBearPower = (series: TechnicalsCandleSeries, length: number) => {
    const emaClose = ema(series.close, length)
    const n = series.close.length
    const computeAt = (idx: number) => {
        const emaVal = emaClose[idx]
        if (Number.isNaN(emaVal)) return null
        return {
            bull: series.high[idx] - emaVal,
            bear: series.low[idx] - emaVal
        }
    }
    const current = computeAt(n - 1)
    const previous = n >= 2 ? computeAt(n - 2) : null
    if (!current) return { extras: {} }
    return {
        value: current.bull + current.bear,
        extras: {
            BULL: current.bull,
            BEAR: current.bear,
            ...(previous
                ? {
                      PREV_BULL: previous.bull,
                      PREV_BEAR: previous.bear
                  }
                : {})
        }
    }
}

export const computeIndicator = (
    spec: TechnicalsIndicatorSpec,
    series: TechnicalsCandleSeries
): IndicatorComputeResult | null => {
    if (!spec.ratingStyle || series.close.length < spec.minCandles) return null

    const inputs = spec.inputs ?? {}
    const source = resolveSource(spec.source || 'CLOSE', series)
    const { high, low, close, volume } = series

    switch (spec.ratingStyle) {
        case 'RSI': {
            const period = inputInt(inputs, 'LENGTH') || 14
            const values = rsi(source, period)
            return { value: last(values), previous: at(values, 1), extras: {} }
        }
        case 'STOCHASTIC': {
            const kPeriod = inputInt(inputs, '%k_length', 'k_length') || 14
            const kSlow = inputInt(inputs, '%k_smoothing', 'k_smoothing') || 3
            const dPeriod = inputInt(inputs, '%d_length', 'd_length') || 3
            const [kSeries, dSeries] = stoch(high, low, close, kPeriod, kSlow, dPeriod)
            return {
                value: last(kSeries),
                previous: at(kSeries, 1),
                extras: { '%D': last(dSeries) ?? NaN }
            }
        }
        case 'CCI': {
            const period = inputInt(inputs, 'LENGTH') || 20
            const values = cci(high, low, close, period)
            return { value: last(values), previous: at(values, 1), extras: {} }
        }
        case 'ADX': {
            const diLen = inputInt(inputs, 'DI_LENGTH') || 14
            const adxLen = inputInt(inputs, 'ADX_SMOOTHING') || 14
            const { adx: adxSeries, plusDI, minusDI } = computeDmi(high, low, close, diLen, adxLen)
            return {
                value: last(adxSeries),
                previous: at(adxSeries, 1),
                extras: {
                    PLUS_DI: last(plusDI) ?? NaN,
                    MINUS_DI: last(minusDI) ?? NaN
                }
            }
        }
        case 'AWESOME_OSCILLATOR': {
            const values = ao(high, low)
            return {
                value: last(values),
                previous: at(values, 1),
                previousPrevious: at(values, 2),
                extras: {}
            }
        }
        case 'MOMENTUM': {
            const period = inputInt(inputs, 'length', 'LENGTH') || 10
            const values = mom(source, period)
            return { value: last(values), previous: at(values, 1), extras: {} }
        }
        case 'MACD': {
            const fast = inputInt(inputs, 'fast_length') || 12
            const slow = inputInt(inputs, 'slow_length') || 26
            const signalPeriod = inputInt(inputs, 'signal_smoothing') || 9
            const [macdLine, signalLine] = macd(source, fast, slow, signalPeriod)
            return {
                value: last(macdLine),
                extras: { signal: last(signalLine) ?? NaN }
            }
        }
        case 'STOCH_RSI':
            return computeStochRsi(close, inputs)
        case 'WILLIAMS_R': {
            const period = inputInt(inputs, 'length', 'LENGTH') || 14
            const values = willr(high, low, close, period)
            return { value: last(values), previous: at(values, 1), extras: {} }
        }
        case 'BULL_BEAR_POWER': {
            const length = inputInt(inputs, 'LENGTH') || 13
            return computeBullBearPower(series, length)
        }
        case 'ULTIMATE_OSCILLATOR': {
            const fast = inputInt(inputs, 'fast_length') || 7
            const middle = inputInt(inputs, 'middle_length') || 14
            const slow = inputInt(inputs, 'slow_length') || 28
            const values = ultosc(high, low, close, fast, middle, slow)
            return { value: last(values), extras: {} }
        }
        case 'ICHIMOKU':
            return computeIchimoku(series, inputs)
        case 'MA_VS_CLOSE': {
            if (spec.id.startsWith('ema_')) {
                const length = inputInt(inputs, 'LENGTH') || 20
                const values = ema(source, length)
                return { value: last(values), extras: {} }
            }
            if (spec.id.startsWith('sma_')) {
                const length = inputInt(inputs, 'LENGTH') || 20
                const values = sma(source, length)
                return { value: last(values), extras: {} }
            }
            if (spec.id === 'vwma_20') {
                const length = inputInt(inputs, 'LENGTH') || 20
                const values = vwma(source, volume, length)
                return { value: last(values), extras: {} }
            }
            if (spec.id === 'hma_9') {
                const length = inputInt(inputs, 'LENGTH') || 9
                const values = hma(source, length)
                return { value: last(values), extras: {} }
            }
            return null
        }
        default:
            return null
    }
}
